import { Chip, IconButton, Menu, MenuItem, Typography } from '@material-ui/core'
import MoreIcon from '@material-ui/icons/MoreVert'
import clsx from 'clsx'
import React, { useState } from 'react'

import {
  canRetire,
  durationParts,
  isCustomerOverride,
  selectorParts,
  SlaPolicy
} from '../../helpers/slaPolicies'
import { formatWhen } from '../../helpers/ticketWorkflow'
import { getTranslate } from '../../localization'
import useStyles from './styles/SlaPoliciesTable.styles'

// Kept in the component rather than the pure helpers: both reach getTranslate,
// and a hardcoded unit letter would render Latin beside Persian digits.
function renderDuration(seconds: number): string {
  const parts = durationParts(seconds)
  if (!parts) return '—'

  return `${parts.value} ${getTranslate(parts.unit)}`
}

function renderSelector(policy: SlaPolicy): string {
  const parts = selectorParts(policy)

  return parts.length ? parts.join(' · ') : getTranslate('all tickets')
}

interface SlaPolicyRowProps {
  policy: SlaPolicy
  retiring: boolean
  onEdit: (policy: SlaPolicy) => void
  onRetire: (policy: SlaPolicy) => void
}

/**
 * One policy version as a div-grid row, with its actions in a three-dot menu.
 *
 * §5: destructive actions live in the row menu, never as a page-level or
 * inline button. Retire was an inline `Button` beside Edit, which put an
 * irreversible action one mis-click from an ordinary one.
 */
export default function SlaPolicyRow({
  policy,
  retiring,
  onEdit,
  onRetire
}: SlaPolicyRowProps) {
  const classes = useStyles()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const closeMenu = () => setAnchorEl(null)
  // Editable whenever the version is live — including the platform catch-all,
  // whose targets can change even though it cannot be retired. Gating edit on
  // canRetire would conflate the two and lock the one policy every ticket
  // falls back to.
  const editable = policy.enabled && !policy.retiredAt
  const retirable = canRetire(policy)

  return (
    <div
      className={clsx(classes.tableRow, {
        [classes.retiredRow]: Boolean(policy.retiredAt)
      })}>
      <div>
        <bdi dir="ltr">{policy.policyId}</bdi>{' '}
        <Typography variant="caption" color="textSecondary">
          <bdi dir="ltr">v{policy.version}</bdi>
        </Typography>
        {isCustomerOverride(policy) && (
          <Chip size="small" color="primary" label={getTranslate('Override')} />
        )}
        {policy.retiredAt && (
          <Chip size="small" label={getTranslate('Retired')} />
        )}
      </div>
      <div>
        <bdi dir="ltr">{renderSelector(policy)}</bdi>
      </div>
      <div>{renderDuration(policy.respondAfterSeconds)}</div>
      <div>{renderDuration(policy.resolveAfterSeconds)}</div>
      <div>{formatWhen(policy.createdAt)}</div>
      <div className={classes.actionsCell}>
        {(editable || retirable) && (
          <>
            {/* Named per row. "More, button" repeated down a column gives a
                screen-reader user no way to tell which policy a menu acts on —
                for a destructive action that is the difference between
                confirming and guessing. */}
            <IconButton
              aria-label={`${getTranslate('Actions')} ${policy.policyId} v${
                policy.version
              }`}
              onClick={(event) => setAnchorEl(event.currentTarget)}>
              <MoreIcon />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={closeMenu}>
              {editable && (
                <MenuItem
                  onClick={() => {
                    closeMenu()
                    onEdit(policy)
                  }}>
                  {getTranslate('Edit')}
                </MenuItem>
              )}
              {retirable && (
                <MenuItem
                  disabled={retiring}
                  onClick={() => {
                    closeMenu()
                    onRetire(policy)
                  }}>
                  {getTranslate('Retire')}
                </MenuItem>
              )}
            </Menu>
          </>
        )}
      </div>
    </div>
  )
}
